import { Leva } from "leva";
import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  type ReactNode,
} from "react";

/**
 * Checks if the 'debug' query string parameter is present in the URL
 */
function isDebugMode(): boolean {
  if (typeof window === "undefined") return false;
  if (import.meta.env.PROD) return false;
  const params = new URLSearchParams(window.location.search);
  return params.has("debug");
}

export type PerformanceTier = "high" | "medium" | "low";

export interface PerformanceInfo {
  tier: PerformanceTier;
  webglSupport: "webgl2" | "webgl1" | "none";
  gpuRenderer: string;
  hardwareConcurrency: number;
  deviceMemory: number | null;
  prefersReducedMotion: boolean;
}

function detectWebGLSupport(): "webgl2" | "webgl1" | "none" {
  try {
    const canvas = document.createElement("canvas");
    if (canvas.getContext("webgl2")) {
      return "webgl2";
    }
    if (canvas.getContext("webgl") || canvas.getContext("experimental-webgl")) {
      return "webgl1";
    }
  } catch {
    // WebGL not available
  }
  return "none";
}

function getGPURenderer(): string {
  try {
    const canvas = document.createElement("canvas");
    const gl = canvas.getContext("webgl2") || canvas.getContext("webgl");
    if (gl) {
      // Plain RENDERER is masked ("WebKit WebGL") in Chrome; the debug
      // extension carries the real string ("ANGLE (... SwiftShader ...)")
      // that software-renderer detection needs. Firefox withholds the
      // extension — those fall through to the FPS-sampler backstop.
      const debug = gl.getExtension("WEBGL_debug_renderer_info");
      if (debug) {
        const unmasked = gl.getParameter(debug.UNMASKED_RENDERER_WEBGL);
        if (unmasked) return String(unmasked);
      }
      return gl.getParameter(gl.RENDERER) || "unknown";
    }
  } catch {
    // Unable to get GPU info
  }
  return "unknown";
}

/**
 * Software rasterizers advertise WebGL but burn CPU per frame — the
 * ambient shader melts GPU-less machines (VMs, CI runners, old
 * laptops). The FPS sampler would catch it eventually; this catches it
 * before first mount.
 */
export function isSoftwareRenderer(renderer: string): boolean {
  return /swiftshader|llvmpipe|softpipe|software|basic render/i.test(renderer);
}

function isDiscreteGPU(renderer: string): boolean {
  const discretePatterns = [
    /nvidia/i,
    /geforce/i,
    /rtx/i,
    /gtx/i,
    /radeon/i,
    /amd/i,
    /rx\s?\d/i,
  ];
  return discretePatterns.some((pattern) => pattern.test(renderer));
}

function getDeviceMemory(): number | null {
  if ("deviceMemory" in navigator) {
    return (
      (navigator as Navigator & { deviceMemory?: number }).deviceMemory ?? null
    );
  }
  return null;
}

function getPrefersReducedMotion(): boolean {
  if (typeof window === "undefined") return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

export function calculateTier(
  info: Omit<PerformanceInfo, "tier">,
): PerformanceTier {
  if (
    info.webglSupport === "none" ||
    info.prefersReducedMotion ||
    isSoftwareRenderer(info.gpuRenderer)
  ) {
    return "low";
  }

  if (
    isDiscreteGPU(info.gpuRenderer) &&
    info.hardwareConcurrency >= 8 &&
    !info.prefersReducedMotion
  ) {
    return "high";
  }

  return "medium";
}

function detectPerformance(): PerformanceInfo {
  const webglSupport = detectWebGLSupport();
  const gpuRenderer = getGPURenderer();
  const hardwareConcurrency = navigator.hardwareConcurrency || 4;
  const deviceMemory = getDeviceMemory();
  const prefersReducedMotion = getPrefersReducedMotion();

  const partialInfo = {
    webglSupport,
    gpuRenderer,
    hardwareConcurrency,
    deviceMemory,
    prefersReducedMotion,
  };

  return {
    ...partialInfo,
    tier: calculateTier(partialInfo),
  };
}

function createReducedMotionListener(
  callback: (prefersReducedMotion: boolean) => void,
): () => void {
  if (typeof window === "undefined") return () => {};

  const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
  const handler = (e: MediaQueryListEvent) => callback(e.matches);

  mediaQuery.addEventListener("change", handler);
  return () => mediaQuery.removeEventListener("change", handler);
}

const STORAGE_KEY = "turtlewave-effects-enabled";
const FPS_THRESHOLD_REDUCE = 30;
const FPS_THRESHOLD_DISABLE = 20;

interface EffectsContextValue {
  /** Whether effects should be rendered */
  effectsEnabled: boolean;
  /** User's manual preference (null = auto) */
  userPreference: boolean | null;
  /** Current performance tier */
  performanceTier: PerformanceTier;
  /** Full performance info */
  performanceInfo: PerformanceInfo | null;
  /** Whether effects were auto-disabled due to poor FPS */
  autoDisabled: boolean;
  /** Toggle effects on/off */
  toggleEffects: () => void;
  /** Set explicit preference */
  setEffectsPreference: (enabled: boolean | null) => void;
  /** Report current FPS for adaptive quality */
  reportFPS: (fps: number) => void;
}

const EffectsContext = createContext<EffectsContextValue | null>(null);

interface EffectsProviderProps {
  children: ReactNode;
}

export function EffectsProvider({ children }: EffectsProviderProps) {
  const [performanceInfo, setPerformanceInfo] =
    useState<PerformanceInfo | null>(null);
  const [userPreference, setUserPreference] = useState<boolean | null>(() => {
    if (typeof window === "undefined") return null;
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === "true") return true;
    if (stored === "false") return false;
    return null;
  });
  const [autoDisabled, setAutoDisabled] = useState(false);
  const [tabVisible, setTabVisible] = useState(
    () => typeof document === "undefined" || !document.hidden,
  );
  const [, setFpsHistory] = useState<number[]>([]);

  // Detect performance on mount
  useEffect(() => {
    const info = detectPerformance();
    setPerformanceInfo(info);

    // Listen for reduced motion preference changes
    const cleanup = createReducedMotionListener((prefersReduced) => {
      setPerformanceInfo((prev) =>
        prev
          ? {
              ...prev,
              prefersReducedMotion: prefersReduced,
              tier: prefersReduced ? "low" : prev.tier,
            }
          : null,
      );
    });

    return cleanup;
  }, []);

  // Track tab visibility — unmount Canvas when hidden, remount fresh when visible
  useEffect(() => {
    const handleVisibility = () => {
      const visible = !document.hidden;
      setTabVisible(visible);

      // Reset auto-disable and FPS history on tab revisit so
      // the shader always gets a fresh chance to render
      if (visible) {
        setAutoDisabled(false);
        setFpsHistory([]);
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibility);
  }, []);

  // Persist user preference
  useEffect(() => {
    if (userPreference === null) {
      localStorage.removeItem(STORAGE_KEY);
    } else {
      localStorage.setItem(STORAGE_KEY, String(userPreference));
    }
  }, [userPreference]);

  // Calculate effective enabled state
  const effectsEnabled = useMemo(() => {
    // Tab not visible — unmount Canvas to free GPU
    if (!tabVisible) return false;

    // User explicitly disabled
    if (userPreference === false) return false;

    // Auto-disabled due to poor performance
    if (autoDisabled && userPreference !== true) return false;

    // Low tier (no WebGL or prefers reduced motion)
    if (performanceInfo?.tier === "low") return false;

    // Default to enabled
    return true;
  }, [tabVisible, userPreference, autoDisabled, performanceInfo?.tier]);

  const performanceTier = performanceInfo?.tier ?? "medium";

  const toggleEffects = useCallback(() => {
    setUserPreference((prev) => {
      if (prev === null) return false; // Auto -> Off
      if (prev === false) return true; // Off -> On
      return null; // On -> Auto
    });
    // Reset auto-disable when user manually toggles
    setAutoDisabled(false);
  }, []);

  const setEffectsPreference = useCallback((enabled: boolean | null) => {
    setUserPreference(enabled);
    if (enabled) {
      setAutoDisabled(false);
    }
  }, []);

  const reportFPS = useCallback((fps: number) => {
    setFpsHistory((prev) => {
      const updated = [...prev, fps].slice(-30); // Keep last 30 samples

      // Calculate rolling average
      const avg = updated.reduce((a, b) => a + b, 0) / updated.length;

      // Auto-disable if consistently poor performance
      if (avg < FPS_THRESHOLD_DISABLE && updated.length >= 10) {
        setAutoDisabled(true);
      } else if (avg >= FPS_THRESHOLD_REDUCE && updated.length >= 10) {
        setAutoDisabled(false);
      }

      return updated;
    });
  }, []);

  const value = useMemo<EffectsContextValue>(
    () => ({
      effectsEnabled,
      userPreference,
      performanceTier,
      performanceInfo,
      autoDisabled,
      toggleEffects,
      setEffectsPreference,
      reportFPS,
    }),
    [
      effectsEnabled,
      userPreference,
      performanceTier,
      performanceInfo,
      autoDisabled,
      toggleEffects,
      setEffectsPreference,
      reportFPS,
    ],
  );

  return (
    <EffectsContext.Provider value={value}>
      <Leva hidden={!isDebugMode()} />
      {children}
    </EffectsContext.Provider>
  );
}

export function useEffects(): EffectsContextValue {
  const context = useContext(EffectsContext);
  if (!context) {
    throw new Error("useEffects must be used within an EffectsProvider");
  }
  return context;
}

export function useEffectsOptional(): EffectsContextValue | null {
  return useContext(EffectsContext);
}
